/*
e.g. CMS Custom Block

<div data-scroll-animation-wrapper data-root-margin="-100%">
    <div
        data-start-offset="0.2"
        data-end-offset="0.8"
        data-animate-from="{ \"opacity\" : \"0\" }"
        data-animate-to="{ \"opacity\" : \"1\" }"
        data-easing="ease-in-out"
    >Hello World</div>
    <img
        data-animate-from="{ \"transform\" : \"translate3d(0, 0, 0) scale(0)\" }"
        data-animate-to="{ \"transform\" : \"translate3d(0, 100vh, 0) scale(1)\" }"
        src="doraemon.jpg"
    >
    <p data-custom-animation="[
        { \"color\": "red", \"offset\" : 0.2 },
        { \"color\": "green", \"offset\" : 0.7 },
        { \"color\": "blue", \"offset\" : 0.95 }
    ]">CASETiFY</p>
</div>

*/

class ScrollViewAnimation {

    constructor(wrapper) {

        this.wrapper = wrapper
        if (wrapper.dataset['scrollAnimationInitialzied']) return

        let elements = Array.from(wrapper.querySelectorAll(`:scope > [data-animate-from][data-animate-to], :scope > [data-custom-animation]`))

        if (elements.length) {
            this.init(elements)
        }
        this.wrapper.dataset['scrollAnimationInitialzied'] = '1'

    }

    init(elements) {

        let animations = []
        let rootMargin = this.wrapper.dataset['rootMargin'] ?? '100%'
        let easing = this.wrapper.dataset['easing'] ?? false

        this.animationTracker = this.wrapper.dataset['animationTracker'] ?? 'on-screen'
        this.printProgress = this.wrapper.dataset['printProgress'] !== undefined

        elements.forEach(element => {

            let from
            let to
            let customAnimation
            let startOffset = 0
            let endOffset = 1
            let easingOverride

            if (element.dataset['startOffset']) {
                startOffset = parseFloat(element.dataset['startOffset'])
            }

            if (element.dataset['endOffset']) {
                endOffset = parseFloat(element.dataset['endOffset'])
            }

            if (element.dataset['easing']) {
                easingOverride = element.dataset['easing']
            }

            try {
                from = element.dataset['animateFrom'] && JSON.parse(element.dataset['animateFrom'])
                to = element.dataset['animateTo'] && JSON.parse(element.dataset['animateTo'])
                customAnimation = element.dataset['customAnimation'] && JSON.parse(element.dataset['customAnimation'])
            } catch(e) {
                throw new Error(`Cannot parse animation settings, invalid JSON`)
            }

            let animationDefinition

            if (customAnimation) {

                animationDefinition = customAnimation

            } else if (from && to) {

                from.offset = startOffset
                to.offset = endOffset

                animationDefinition = [from, to]
                if (startOffset !== 0) {
                    let dummyFrom = structuredClone(from)
                    dummyFrom.offset = 0
                    animationDefinition = [dummyFrom, ...animationDefinition]
                }

                if (endOffset !== 1) {
                    let dummyTo = structuredClone(to)
                    dummyTo.offset = 1
                    animationDefinition = [...animationDefinition, dummyTo]
                }

            } else {

                throw new Error(`Cannot parse animation settings, settings empty`)

            }

            let animationOption = {
                duration: 100,
                fill: 'forwards'
            }

            if (easingOverride) animationOption.easing = easingOverride
            else if (easing) animationOption.easing = easing

            let animation = element.animate(animationDefinition, animationOption)

            animation.pause()
            animations.push(animation)

        })

        let options = {
            rootMargin,
            threshold: 0
        }

        let scrollHanlder = evt => {

            let currentTime = this.getProgress() * 100
            animations.forEach(anim => {
                anim.currentTime = currentTime
            })
        }

        let observerCallback = (entries, observer) => {
            let {
                isIntersecting
            } = entries[0]
            if (isIntersecting) {
                window.addEventListener('scroll', scrollHanlder, { passive: true })
                scrollHanlder()
            } else {
                scrollHanlder()
                window.removeEventListener('scroll', scrollHanlder)
            }
        }

        this.observer = new IntersectionObserver(observerCallback, options)
        this.observer.observe(this.wrapper)
        scrollHanlder()

    }

    getProgress() {

        let rect = this.wrapper.getBoundingClientRect()
        if (!rect) return 0

        let wh = window.innerHeight
        let progress = 0

        switch (this.animationTracker) {
            case 'top-edge':
                progress = getStartPointProgress(rect)
                break
            case 'bottom-edge':
                progress = getEndPointProgress(rect)
                break
            case 'mid-point':
                progress = getMidPointProgress(rect)
                break
            case 'off-screen':
                progress = getNearEdgeProgress(rect)
                break
            case 'on-screen' :
            default:
                progress = getFarEdgeProgress(rect)
        }

        if (this.printProgress) {
            this.wrapper.dataset['animationProgress'] = progress
        }

        return progress

        function getScrollProgress(yPos, wh) {
            wh ??= window.innerHeight
            let progress = 1 - (yPos / wh)
            if (progress < 0) return 0
            if (progress > 1) return 1
            return progress
        }

        function getNearEdgeProgress(rect) {
            if (rect.top > wh) return 0
            else if (rect.bottom < 0) return 1

            let scrollableHeight = wh + rect.height
            // let r = scrollableHeight / wh
            let normalizedTop = (rect.top + rect.height)
            // let normalizedBottom = rect.bottom
            let result = 1 - (normalizedTop / scrollableHeight)

            // console.table({
            //     scrollableHeight,
            //     r,
            //     normalizedTop,
            //     normalizedBottom,
            //     wh,
            //     top: rect.top,
            //     bottom: rect.bottom
            // })

            if (result > 1) return 1
            else if (result < 0) return 0
            return result
        }

        function getFarEdgeProgress(rect) {
            if (rect.bottom > wh) return 0
            else if (rect.bottom < rect.height) return 1

            let scrollableHeight = wh - rect.height
            let r = scrollableHeight / wh
            let normalizedTop = rect.top / r
            let result = 1 - (normalizedTop / wh)
            if (result > 1) return 1
            else if (result < 0) return 0
            return result
        }

        function getStartPointProgress(rect) {
            return getScrollProgress(rect.top)
        }

        function getEndPointProgress(rect) {
            return getScrollProgress(rect.bottom)
        }

        function getMidPointProgress(rect) {
            return getScrollProgress((rect.top + rect.bottom) / 2)
        }

    }

}

class VideoLottie {

    constructor(wrapper) {

        this.wrapper = wrapper
        if (wrapper.dataset['videoLottieInitialzied']) return
        this.videoEl = wrapper.querySelector(':scope > video')

        this.init()
        this.wrapper.dataset['videoLottieInitialzied'] = '1'

        // For Debug
        // window.videoLotties ??= []
        // window.videoLotties.push(this)

    }

    async init() {

        if (!this.videoEl) return

        this.videoEl.remove()
        this.videoEl.loop = false
        this.videoEl.playsInline = true
        this.canvas = document.createElement('canvas')
        this.canvas.style.cssText = `
            top: 0;
            position: sticky;
            background-repeat: no-repeat;
            background-position: center center;
            background-size: cover;
            width: 100%;
            height: 100vh;
            object-fit: cover;
            pointer-events: none;
            z-index: -1;
        `
        this.ctx = this.canvas.getContext('2d', { willReadFrequently : true })

        let { width, height, duration } = await this.queryVideoInfo()
        this.canvas.width = width
        this.canvas.height = height
        this.duration = duration
        this.fnDraw = null
        this.wrapper.appendChild(this.canvas)

        let scrollHanlder = evt => {
            this.displayImage()
        }

        let observerCallback = (entries, observer) => {
            let {
                isIntersecting
            } = entries[0]
            if (isIntersecting) {
                try {
                    if (this.videoEl.paused) {
                        this.videoEl.play()
                    }
                } catch(e) {
                    // do nothing
                }
                window.addEventListener('scroll', scrollHanlder, { passive: true })
                scrollHanlder()
            } else {
                scrollHanlder()
                this.videoEl.pause()
                window.removeEventListener('scroll', scrollHanlder)
            }
        }

        this.observer = new IntersectionObserver(observerCallback)
        this.observer.observe(this.wrapper)

        // window.addEventListener('scroll', scrollHanlder, { passive: true })
        if (this.wrapper.getBoundingClientRect().width > 0) this.displayImage(0)

    }

    displayImage(targetTime = undefined) {

        let time = this.getTime()
        if (time === -1) return

        this.videoEl.addEventListener('seeked', () => {
            this.fnDraw && clearTimeout(this.fnDraw)
            this.fnDraw = setTimeout(() => this.ctx.drawImage(this.videoEl, 0, 0), 16.67)
        }, { once: true })

        this.videoEl.currentTime = targetTime ?? time
        this.ctx.drawImage(this.videoEl, 0, 0)
        return

    }

    queryVideoInfo() {

        let el = this.videoEl
        let { videoWidth, videoHeight } = el

        return new Promise((resolve, reject) => {

          if (videoWidth === 0 || videoHeight === 0) {
            el.addEventListener('loadedmetadata', evt => {
              resolve({
                width : el.videoWidth,
                height : el.videoHeight,
                duration : el.duration
              })
            })
          }

          else resolve({
            width : videoWidth,
            height : videoHeight,
            duration : el.duration
          })

        })

    }

    getTime(delta = 0) {

        let rect = this.wrapper.getBoundingClientRect()
        if (!rect) return -1
        if (rect.width === 0 || rect.height === 0) return -1

        let wh = window.innerHeight
        let { top, height } = rect
        let r = ((wh - (top + wh)) / (height - wh) * this.duration)
        if (r < 0) return 0
        if (r > this.duration) return this.duration
        return r

    }

}

module.exports = {
    ScrollViewAnimation,
    VideoLottie
}


// WEBPACK FOOTER //
// ./src/view-animation.js